import { marketLocationRevenueQuarterlyReport, marketLocationRevenueReport, root } from '@e2m-customer-portal/core';
import { MonthData, MonthlyRevenue, QuarterlyRevenue, RevenueDownloadDrawer } from '@e2m-customer-portal/domains';
import { useLocale, useMarketLocation, useMarketLocationId } from '@e2m-customer-portal/hooks';
import { isBattery, isCoLocation, isSolar, isWind } from '@e2m-customer-portal/shared';
import { ErrorComponent, ModuleTabs, SweptTab } from '@e2m-customer-portal/ui';
import { Button } from '@mui/material';
import { startOfMonth } from 'date-fns';
import { createContext, Dispatch, ReactElement, SetStateAction, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, Navigate, Route, Routes, useLocation } from 'react-router';

export const SelectedMonthContext = createContext<{
  selectedMonth: MonthData;
  setSelectedMonth: Dispatch<SetStateAction<MonthData>>;
}>({
  selectedMonth: { id: '', date: new Date(), name: '' },
  setSelectedMonth: () => ({ id: '', date: new Date(), name: '' }),
});

interface RevenueReportPageProps {
  sidebarOffset?: number;
}

export function RevenueReportPage({
  sidebarOffset = 0,
}: Readonly<RevenueReportPageProps>): ReactElement<RevenueReportPageProps> {
  const [t] = useTranslation();
  const { pathname } = useLocation();
  const marketLocationId = useMarketLocationId();
  if (marketLocationId == null) {
    throw new Error('Failed to retrieve market location id');
  }

  const [selectedMonth, setSelectedMonth] = useState<MonthData>(
    (() => {
      const { code: locale } = useLocale();
      return {
        id: startOfMonth(new Date()).toISOString(),
        date: startOfMonth(new Date()),
        name: `${new Date().toLocaleDateString(locale, { month: 'long', year: 'numeric' })}`,
      };
    })(),
  );

  const selectValue = useMemo(
    () => ({
      selectedMonth,
      setSelectedMonth,
    }),
    [selectedMonth, setSelectedMonth],
  );

  const [isOpen, setIsOpen] = useState(false);
  const pageTitleActions = (
    <>
      <RevenueDownloadDrawer open={isOpen} onClose={() => setIsOpen(false)} marketLocationId={marketLocationId} />
      <Button onClick={() => setIsOpen(true)}>{t('REVENUE_DOWNLOAD_BUTTON')}</Button>
    </>
  );

  const [marketLocation, error] = useMarketLocation(marketLocationId);

  if (!marketLocation || error) {
    return <ErrorComponent error={error} />;
  }

  const { permissions, technologies } = marketLocation;

  if (!permissions.seeRevenue || isWind(technologies)) {
    return <Navigate to={root.getLink()} replace />;
  }

  return (
    <div className="flex flex-col">
      <ModuleTabs
        scrollButtons={false}
        value={pathname}
        className="w-fit origin-bottom-left scale-75 md:w-full md:transform-none"
      >
        <SweptTab
          component={Link}
          to={marketLocationRevenueReport.getLink(marketLocationId)}
          value={decodeURIComponent(marketLocationRevenueReport.getLink(marketLocationId))}
          label={t('REVENUE_REPORT_TAB_MONTHLY_REVENUE_REPORT')}
        />

        {!(isBattery(technologies) || isCoLocation(technologies)) && (
          <SweptTab
            component={Link}
            to={marketLocationRevenueQuarterlyReport.getLink(marketLocationId)}
            value={decodeURIComponent(marketLocationRevenueQuarterlyReport.getLink(marketLocationId))}
            label={t('REVENUE_REPORT_TAB_QUARTERLY_REVENUE_REPORT')}
          />
        )}
      </ModuleTabs>

      <SelectedMonthContext value={selectValue}>
        <Routes>
          <Route
            path="/"
            element={
              <MonthlyRevenue
                marketLocationId={marketLocationId}
                pageTitleActions={!isSolar(technologies) && pageTitleActions}
                sidebarOffset={sidebarOffset}
              />
            }
          />

          {!isBattery(technologies) && (
            <Route
              path={marketLocationRevenueQuarterlyReport.relativePath}
              element={
                <QuarterlyRevenue
                  marketLocationId={marketLocationId}
                  pageTitleActions={!isSolar(technologies) && pageTitleActions}
                  sidebarOffset={sidebarOffset}
                />
              }
            />
          )}
          <Route
            path="*"
            element={
              <Navigate
                to={marketLocationId ? marketLocationRevenueReport.getLink(marketLocationId) : root.getLink()}
                replace
              />
            }
          />
        </Routes>
      </SelectedMonthContext>
    </div>
  );
}
